/**
 * Matching Engine Admin Participant Snapshot schemas
 *
 * Validation schemas for the admin UI per-participant drill-down:
 *   GET /api/v1/admin/events/:eventIdPublic/matching-engine/runs/:runIdPublic/participants/:participantIdPublic
 *
 * Plain Zod schemas with a single inferred type (no transform); dates use
 * `z.coerce.date()` and the person input uses `serializableValueSchema`.
 *
 * Four sections: `input` — the engine's `PersonInput` for this participant (the
 * same collect the matching pipeline is fed, not the participant DB record),
 * crossing the wire as a free-form object so new engine input fields surface
 * without a schema change; `enrichment` — requests surface only their
 * public id (the entity holds a Clay secret) and enriched user/company data are
 * projected to the secret-free `EnrichedUserDataView` / `EnrichedCompanyView`
 * (the raw provider payload is stripped); `stepResults` — every raw step-result
 * for this participant in the run, carried as the canonical domain union
 * verbatim (no curated allow-list, no composites) so a new step type surfaces
 * with zero change here; `output` — the single legitimate fold (base pick +
 * overlays) with provenance, whose schema lives in the sibling
 * `picks-derived.schema.ts`.
 */

import { z } from "zod";
import { serializableValueSchema } from "./serializable-value.schemas.js";
import { AdminParticipantEnrichmentSchema } from "./admin-participant-enrichment.schemas.js";
import { MatchingEngineStepResultSchema } from "./matching-engine-step-result.schemas.js";
import { picksDerivedSchema } from "./picks-derived.schemas.js";

export const participantSnapshotPersonInputSchema = z.record(
  z.string(),
  serializableValueSchema,
);

// The snapshot exposes three special (non-step) sections — `input`,
// `enrichment`, `output` — plus every raw step-result for this participant in
// the selected run. `stepResults` is the canonical domain union verbatim (no
// curated allow-list, no composites); `output` is the single legitimate fold.
export const MatchingEngineParticipantSnapshotSchema = z.object({
  input: participantSnapshotPersonInputSchema,
  enrichment: AdminParticipantEnrichmentSchema,
  stepResults: z.array(MatchingEngineStepResultSchema),
  output: picksDerivedSchema.nullable(),
});

export type ParticipantSnapshotPersonInput = z.infer<
  typeof participantSnapshotPersonInputSchema
>;
export type MatchingEngineParticipantSnapshot = z.infer<
  typeof MatchingEngineParticipantSnapshotSchema
>;
